import React from 'react';

import { flexRender, HeaderGroup, RowModel } from '@tanstack/react-table';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Table,
  TableBody,
  TableCell,
  TableCellSort,
  TableHead,
  TableRow,
} from '@rrexpress/core';

export interface ITableComponentActiveSort {
  columnIndex: number;
  columnName: string;
  sortType: 'ASC' | 'DSC' | 'DEFAULT'
}

export interface TableComponentProps {
  getHeaderGroups: () => HeaderGroup<any>[]; // Define more specific types based on your usage
  getRowModel: () => RowModel<any>; // Define more specific types based on your usage
  activeSort: ITableComponentActiveSort | undefined;
  onSortChange: (obj: ITableComponentActiveSort) => void
}

export interface ITablePaginationProps {
  getState: () => any; // Define more specific types based on your usage
  setPageIndex: (pageIndex: number) => void;
  getCanPreviousPage: () => boolean;
  previousPage: () => void;
  nextPage: () => void;
  getCanNextPage: () => boolean;
  getPageCount: () => number;
  setPageSize: (size: number) => void;
  pageSizeOptions: number[];
}

export const TableComponent: React.FC<TableComponentProps> = ({
  getHeaderGroups, getRowModel, activeSort, onSortChange,
}) => (
  <div style={{ maxWidth: '100%', overflowY: 'scroll' }}>
    <Table style={{ display: 'inline-table' }}>
      <TableHead sticky>
        {getHeaderGroups()
          .map((headerGroup: { id: React.Key | null | undefined; headers: any[]; }) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, idx) => (
                <>
                  {activeSort && (
                    <TableCellSort
                      key={header.id}
                      active={activeSort.columnIndex === idx}
                      onRequestSort={(name, sortType) => onSortChange({
                        columnIndex: idx,
                        columnName: name,
                        sortType,
                      })}
                      name={header.column.id}
                    >
                      {header.column.columnDef.header}
                    </TableCellSort>
                  )}

                  {!activeSort && (
                    <TableCell
                      key={header.id}
                    >
                      {header.column.columnDef.header}
                    </TableCell>
                  )}
                </>

              ))}
            </TableRow>
          ))}
      </TableHead>
      <TableBody>
        {getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell className="align-top" key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const PaginationControls: React.FC<ITablePaginationProps> = ({
  getState,
  setPageIndex,
  getCanPreviousPage,
  previousPage,
  nextPage,
  getCanNextPage,
  getPageCount,
  setPageSize,
  pageSizeOptions,
}) => (
  <div className="flex mt-4 justify-between items-center">
    <div className="flex items-center space-x-2">
      <span>
        Tampilkan
      </span>
      <select
        className="border border-blue-400 p-1 px-2 bg-white rounded-lg focus-visible:outline-none"
        value={getState().pagination.pageSize}
        onChange={(e) => setPageSize(Number(e.target.value))}>
        {pageSizeOptions.map((pageSize) => (
          <option key={pageSize} value={pageSize}>
            {pageSize}
          </option>
        ))}
      </select>
      <span>
        baris
      </span>
    </div>

    <div className="flex items-center space-x-2">
      <button
        className="px-1 rounded-md disabled:cursor-not-allowed"
        onClick={() => setPageIndex(0)}
        disabled={!getCanPreviousPage()}
      >
        <ChevronDoubleLeftIcon />
      </button>
      <button
        className="px-1 rounded-md disabled:cursor-not-allowed"
        onClick={previousPage}
        disabled={!getCanPreviousPage()}
      >
        <ArrowLeftIcon />
      </button>

      <span className="mx-2">
        <strong>{getState().pagination.pageIndex + 1}</strong>
      </span>

      <button
        className="px-1 rounded-md disabled:cursor-not-allowed"
        onClick={nextPage}
        disabled={!getCanNextPage()}
      >
        <ArrowRightIcon />
      </button>
      <button
        className="px-1 rounded-md disabled:cursor-not-allowed"
        onClick={() => setPageIndex(getPageCount() - 1)}
        disabled={!getCanNextPage()}
      >
        <ChevronDoubleRightIcon />
      </button>
    </div>
  </div>
);
